import type * as React from 'react';
import type { ContentExtraProps } from '../types';
import {
  AnnualLeaveSlot,
  CancelTargetSlot,
  OvertimeApplySlot,
  ToilTimeUseSlot,
  WorkingHoursChangeSlot,
} from './attendance';
import { AnnualLeaveRequestSlot } from './annualLeaveRequest';
import { ContractSlot } from './contract';
import { CorporateSealSlot } from './corporateSeal';
import { EntExpenseSlot } from './entExpense';
import { ExpensePreApprovalSlot } from './expensePreApproval';
import { FncoPreMarketingSlot } from './fncoPreMarketing';
import { MarketingExpenseSlot } from './marketingExpense';
import { OpenConstructionCostSlot } from './openConstructionCost';
import { ResignationSlot } from './resignation';
import { LongServiceLeaveSlot } from './longServiceLeave';
import { NewCorporateCardSlot } from './newCorporateCard';
import { BpRegistrationSlot } from './bpRegistration';
import { ProbationEvaluationSlot } from './probationEvaluation';
import { SalesBrandPermissionSlot } from './salesBrandPermission';
import { SaVoucherSlot } from './saVoucher';
import { ShopManagerFeeSlot } from './shopManagerFee';
import { SapContractFeeSlot } from './sapContractFee';
import { SerpStockAdjustmentSlot } from './serpStockAdjustment';
import { SerpStoreChangeSlot } from './serpStoreChange';
import { SerpStoreOpenSlot } from './serpStoreOpen';
import { SerpStoreReturnSlot } from './serpStoreReturn';
import { SerpLendingDelaySlot } from './serpLendingDelay';
import { SerpStoreClosureSlot } from './serpStoreClosure';
import { SerpIntermediaryReplaceSlot } from './serpIntermediaryReplace';
import { SerpSellingPriceSlot } from './serpSellingPrice';
import { SerpSeasonPlanSlot } from './serpSeasonPlan';
import { SerpBulkStockAdjustmentSlot } from './serpBulkStockAdjustment';
import { SerpSeasonEndSlot } from './serpSeasonEnd';
import { SerpApStockTransferSlot } from './serpApStockTransfer';
import { ProductionCostSlot } from './productionCost';
import { ExternalLoanSlot } from './externalLoan';
import { MortgageFeeSlot } from './mortgageFee';
import { StoreOpenSlot } from './storeOpen';
import { GroupSalesSlot } from './groupSales';
import { GroupSalesMultiSlot } from './groupSalesMulti';
import { MarketingCaseSlot } from './marketingCase';
import { CorporateCardRequestSlot } from './corporateCardRequest';
import { DiscountVoucherSlot } from './discountVoucher';
import { ExpenseVoucherSlot } from './expenseVoucher';
import { SapBondStatusSlot } from './sapBondStatus';
import { SapBudgetIncreaseSlot } from './sapBudgetIncrease';
import { SapCollateralChangeSlot } from './sapCollateralChange';
import { AccountingBpSlot } from './accountingBp';
import { InfluencerPplSlot } from './influencerPpl';
import { InfluencerMonthlyReportSlot } from './influencerMonthlyReport';
import { SaTravelExpenseSlot, SaTravelRequestSlot } from './saTravelExpense';
import { FncoPriceSupplySlot } from './fncoPriceSupply';
import { ProductionVendorSlot } from './productionVendor';
import { ProductLifecycleSlot } from './productLifecycle';
import { StyleMasterPriceSlot } from './styleMasterPrice';
import { ProductionCostClosingSlot } from './productionCostClosing';
import { ClaimClosingSlot } from './claimClosing';
import { InventoryReplenishmentSlot } from './inventoryReplenishment';
import { StorageMaterialOrderSlot } from './storageMaterialOrder';
import { SupplierEvaluationSlot } from './supplierEvaluation';
import { PurchaseRequisitionSlot } from './purchaseRequisition';
import { CostQuotationSlot } from './costQuotation';
import { SalesContractSlot } from './salesContract';
import { CreditNoteSlot } from './creditNote';
import { ClaimNoteSlot } from './claimNote';
import { VehicleCostSlot } from './vehicleCost';
import { WorkSpecificationSlot } from './workSpecification';

export {
  isAttendanceContentType,
  isSelfApprovalAttendanceType,
  useAttendanceCancel,
} from './attendance';

/**
 * `apvType` → 본문 추가 컴포넌트 매핑. 매칭 없으면 `null`.
 * 1번(기본품의) 유형은 추가 영역이 없어 항상 null. 신규 유형은 case 한 줄 추가.
 */
export const renderContentExtra = (
  apvType: number,
): React.FC<ContentExtraProps> | null => {
  switch (apvType) {
    case 222:
      return WorkingHoursChangeSlot;
    case 223:
      return OvertimeApplySlot;
    case 224:
      return ToilTimeUseSlot;
    case 231:
      return AnnualLeaveSlot;
    case 32:
      return AnnualLeaveRequestSlot;
    case 226:
    case 227:
    case 232:
    case 237:
    case 238:
    case 239:
      return CancelTargetSlot;
    case 117:
    case 118:
      return ContractSlot;
    case 99:
      return SaVoucherSlot;
    case 185:
      return SapBondStatusSlot;
    case 187:
      return SapBudgetIncreaseSlot;
    case 154:
    case 155:
      return SapCollateralChangeSlot;
    case 101:
      return AccountingBpSlot;
    case 135:
      return InfluencerPplSlot;
    case 108:
      return InfluencerMonthlyReportSlot;
    case 177:
      return SaTravelExpenseSlot;
    case 179:
      return SaTravelRequestSlot;
    case 197:
    case 198:
    case 199:
      return FncoPriceSupplySlot;
    case 68:
      return DiscountVoucherSlot;
    case 111:
      return ExpenseVoucherSlot;
    case 205:
      return ExpensePreApprovalSlot;
    case 233:
    case 234:
      return OpenConstructionCostSlot;
    case 221:
      return FncoPreMarketingSlot;
    case 212:
      return MarketingExpenseSlot;
    case 213:
      return EntExpenseSlot;
    case 183:
      return WorkSpecificationSlot;
    case 93:
      return VehicleCostSlot;
    case 196:
      return ResignationSlot;
    case 240:
      return LongServiceLeaveSlot;
    case 204:
      return NewCorporateCardSlot;
    case 200:
    case 201:
      return BpRegistrationSlot;
    case 113:
      return CorporateSealSlot;
    case 157:
      return ShopManagerFeeSlot;
    case 167:
      return SapContractFeeSlot;
    case 169:
      return SerpStockAdjustmentSlot;
    case 162:
      return SerpStoreChangeSlot;
    case 161:
      return SerpStoreOpenSlot;
    case 171:
      return SerpStoreReturnSlot;
    case 163:
      return SerpLendingDelaySlot;
    case 181:
      return SerpStoreClosureSlot;
    case 182:
      return SerpIntermediaryReplaceSlot;
    case 160:
      return SerpSellingPriceSlot;
    case 159:
      return SerpSeasonPlanSlot;
    case 210:
      return SerpBulkStockAdjustmentSlot;
    case 174:
      return SerpSeasonEndSlot;
    case 170:
      return SerpApStockTransferSlot;
    case 216:
      return ProductionCostSlot;
    case 217:
      return ExternalLoanSlot;
    case 81:
      return MortgageFeeSlot;
    case 8:
      return StoreOpenSlot;
    case 206:
    case 207:
      return CorporateCardRequestSlot;
    case 188:
      return ProbationEvaluationSlot;
    case 215:
      return SalesBrandPermissionSlot;
    case 61:
      return GroupSalesSlot;
    case 131:
      return GroupSalesMultiSlot;
    case 83:
      return MarketingCaseSlot;
    case 120:
      return ProductionVendorSlot;
    case 123:
      return ProductLifecycleSlot;
    case 128:
      return StyleMasterPriceSlot;
    case 124:
      return ProductionCostClosingSlot;
    case 186:
      return ClaimClosingSlot;
    case 184:
      return InventoryReplenishmentSlot;
    case 125:
      return StorageMaterialOrderSlot;
    case 127:
      return SupplierEvaluationSlot;
    case 122:
    case 129:
      return PurchaseRequisitionSlot;
    case 121:
      return CostQuotationSlot;
    case 136:
      return SalesContractSlot;
    case 156:
      return CreditNoteSlot;
    case 166:
      return ClaimNoteSlot;
    default:
      return null;
  }
};
